import { BooleanField, Float32Field, Int32Field, StringField } from "@opendaw/lib-box";
import { clamp, isNull, isUndefined, panic } from "@opendaw/lib-std";
const hasValues = (constraints) => typeof constraints === "object" && Object.hasOwn(constraints, "values");
const hasLength = (constraints) => typeof constraints === "object" && Object.hasOwn(constraints, "length");
export var Guard;
(function (Guard) {
    Guard.number = (value, name) => {
        if (typeof value !== "number") {
            return panic(new TypeError(`${name}: expected a number, got ${Guard.describe(value)}`));
        }
        if (Number.isNaN(value)) {
            return panic(new TypeError(`${name}: NaN is not a valid value`));
        }
        return value;
    };
    Guard.finite = (value, name) => {
        const result = Guard.number(value, name);
        if (!Number.isFinite(result)) {
            return panic(new RangeError(`${name}: ${result} is not finite`));
        }
        return result;
    };
    Guard.integer = (value, name) => Math.round(Guard.finite(value, name));
    Guard.boolean = (value, name) => {
        if (typeof value !== "boolean") {
            return panic(new TypeError(`${name}: expected a boolean, got ${Guard.describe(value)}`));
        }
        return value;
    };
    Guard.string = (value, name) => {
        if (typeof value !== "string") {
            return panic(new TypeError(`${name}: expected a string, got ${Guard.describe(value)}`));
        }
        return value;
    };
    Guard.oneOf = (value, values, name) => {
        if (!values.includes(value)) {
            return panic(new RangeError(`${name}: ${Guard.describe(value)} is not one of ${values.map(String).join(", ")}`));
        }
        return value;
    };
    Guard.float32 = (constraints, value, name) => {
        if (constraints === "any") {
            return Guard.finite(value, name);
        }
        if (constraints === "unipolar") {
            return clamp(Guard.finite(value, name), 0.0, 1.0);
        }
        if (constraints === "bipolar") {
            return clamp(Guard.finite(value, name), -1.0, 1.0);
        }
        if (constraints === "decibel") {
            const result = Guard.number(value, name);
            if (result === Number.POSITIVE_INFINITY) {
                return panic(new RangeError(`${name}: +Infinity is not a valid gain`));
            }
            return Math.min(result, 0.0);
        }
        if (constraints === "non-negative") {
            return Math.max(Guard.finite(value, name), 0.0);
        }
        if (constraints === "positive") {
            const result = Guard.finite(value, name);
            if (result <= 0.0) {
                return panic(new RangeError(`${name}: ${result} must be positive`));
            }
            return result;
        }
        return clamp(Guard.finite(value, name), constraints.min, constraints.max);
    };
    Guard.int32 = (constraints, value, name) => {
        const result = Guard.integer(value, name);
        if (constraints === "any") {
            return result;
        }
        if (constraints === "index" || constraints === "non-negative") {
            return Math.max(result, 0);
        }
        if (constraints === "positive") {
            if (result <= 0) {
                return panic(new RangeError(`${name}: ${result} must be positive`));
            }
            return result;
        }
        if (hasValues(constraints)) {
            return Guard.oneOf(result, constraints.values, name);
        }
        if (hasLength(constraints)) {
            if (result < 0 || result >= constraints.length) {
                return panic(new RangeError(`${name}: ${result} must be in [0, ${constraints.length - 1}]`));
            }
            return result;
        }
        return clamp(result, constraints.min, constraints.max);
    };
    Guard.field = (field, value, name) => {
        if (field instanceof Float32Field) {
            return Guard.float32(field.constraints, value, name);
        }
        if (field instanceof Int32Field) {
            return Guard.int32(field.constraints, value, name);
        }
        if (field instanceof BooleanField) {
            return Guard.boolean(value, name);
        }
        if (field instanceof StringField) {
            return Guard.string(value, name);
        }
        return panic(`${name}: unsupported field type`);
    };
    Guard.describe = (value) => {
        if (isNull(value)) {
            return "null";
        }
        if (isUndefined(value)) {
            return "undefined";
        }
        if (typeof value === "string") {
            return `"${value}"`;
        }
        if (typeof value === "object") {
            return value.constructor?.name ?? "object";
        }
        return String(value);
    };
})(Guard || (Guard = {}));
